/**
 * BuildMenu.tsx — WEB.4.UI.4 / R11.2.6 discount picker
 *
 * Modal shown when the current player lands on a buildable tile
 * (Urban / Plains / Desert / Coast / River / Tundra / Mountain / Forest / Volcanic).
 *
 * Surfaces:
 *   - Discount picker pill bar (R11.2.6) — only when PEQ has eligible discount entries
 *   - 6 primary facility rows (coal / oil / hydro / wind / solar / nuclear)
 *   - Each row: icon | name+stats | cost | Build button
 *   - Locked rows when facility is biome-illegal or tile already occupied
 *   - Footer: Skip Build button
 *
 * VISIBLE WHEN: buildMenuOpen === true
 * DISCOUNT PICKER VISIBLE WHEN: peqState has NextBuildFree / NextSolarFree /
 *   NextBuildDiscount / NextNuclearDiscount entry for current seat
 *
 * Adders deferred to future iteration:
 *   - Aura preview side panel (hover) — Adder 1
 *   - Facility place animation — Adder 2
 */

import type { AuraManagerState } from "@gdg/rules-engine";
import type { PendingKind } from "@gdg/shared";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import tilePositionsData from "../../../../../data/board/tile-positions.json";
import { playSfx } from "../../audio/audio.js";
import { translate } from "../../i18n/i18n.js";
import {
  DISCOUNT_SKIP,
  computeBuildCostBreakdown,
  getEligibleDiscounts,
} from "../../store/aura-build-cost.js";
import type { BuildCostBreakdown, DiscountOverride } from "../../store/aura-build-cost.js";
import {
  computeEnergyTick,
  computePollutionTick,
  computeTollOnLanding,
} from "../../store/aura-tick.js";
import { FACILITIES, FACILITY_TYPE_TO_ID } from "../../store/facilities.js";
import type { FacilityDef } from "../../store/facilities.js";
import { getAllFacilitiesForTerrain } from "../../store/facility-rules.js";
import type { FacilityRule } from "../../store/facility-rules.js";
import { useGameStore } from "../../store/game-store.js";
import type { SeatIndex } from "../../store/game-store.js";
import styles from "./BuildMenu.module.css";

// ---------------------------------------------------------------------------
// Tile type lookup from tile-positions.json
// ---------------------------------------------------------------------------

type TileEntry = { index: number; type: string; x: number; y: number };
const _tileTypeByIndex: Record<number, string> = Object.fromEntries(
  (tilePositionsData.tiles as TileEntry[]).map((t) => [t.index, t.type]),
);

// ---------------------------------------------------------------------------
// Seat config helpers
// ---------------------------------------------------------------------------

const SEAT_CHIP_CLASSES = [
  styles.seatChip,
  styles.seatChipP2,
  styles.seatChipP3,
  styles.seatChipP4,
] as const;
const SEAT_HEADER_CLASSES = [
  styles.header,
  `${styles.header} ${styles.headerP2}`,
  `${styles.header} ${styles.headerP3}`,
  `${styles.header} ${styles.headerP4}`,
] as const;
const SEAT_DISPLAY = ["P1 Active", "P2 Active", "P3 Active", "P4 Active"] as const;

// ---------------------------------------------------------------------------
// FacilityRow sub-component
// ---------------------------------------------------------------------------

interface FacilityRowProps {
  /** Era-specific build rule from facility-rules.json (cost/toll/pollution/energy/label). */
  rule: FacilityRule;
  /** Static FacilityDef fallback (icon/category/category-CSS). May be undefined for unmapped types. */
  facility: FacilityDef | undefined;
  /** id used for builtFacilities[tileIdx] storage + aura lookups. */
  facilityId: string;
  tileAlreadyBuilt: boolean;
  existingFacilityName: string | undefined;
  tokens: number;
  /** WEB.4.UI.8b: pre-computed aura-modified cost breakdown for this facility+seat. */
  breakdown: BuildCostBreakdown;
  /** Bug #10: active aura state — used to compute aura-modified energy/pollution/toll. */
  auraState: AuraManagerState;
  /** Bug #10: current seat index — scopes aura queries per-seat. */
  seatIdx: number;
  /** Current era — captured into per-tile metadata on build (WEB.4.UI.11c). */
  era: 1 | 2 | 3 | 4;
  /** Current terrain — captured into per-tile metadata on build (WEB.4.UI.11c). */
  terrain: string;
  /** R3 Bug 3: era in which this facility first unlocks. If > current era, row is era-locked. */
  lockedUntilEra?: 1 | 2 | 3 | 4;
  /** R3 Bug 3: UI language for locked-era badge translation. */
  lang: "en" | "th";
  /** R20-fix (codex-review #4): Hydro-block remaining turns for this seat. 0 = no block. */
  hydroBlockTurns?: number;
  /**
   * R11.2.6: the discount kind the player has selected (or null/skip).
   * Forwarded to buildFacility so the store can drain the correct PEQ entry.
   * null = auto-best, "skip" = no discount consumed.
   */
  selectedDiscountKind: PendingKind | null;
  onBuild: (
    facilityId: string,
    cost: number,
    meta: {
      energyPerTurn: number;
      pollutionPerTurn: number;
      tollPerLand: number;
      builtInEra: 1 | 2 | 3 | 4;
      builtOnTerrain: string;
    },
    chosenDiscountKind: PendingKind | null,
  ) => void;
}

function FacilityRow({
  rule,
  facility,
  facilityId,
  tileAlreadyBuilt,
  existingFacilityName,
  tokens,
  breakdown,
  auraState,
  seatIdx,
  era,
  terrain,
  lockedUntilEra,
  lang,
  hydroBlockTurns = 0,
  selectedDiscountKind,
  onBuild,
}: FacilityRowProps): React.ReactElement {
  // R3 Bug 3: era-locked rows are visible but disabled.
  const isEraLocked = lockedUntilEra !== undefined && lockedUntilEra > era;
  // R20-fix: Drought TurnBlock — disable Hydro rows when block is active.
  const isHydroBlocked = facilityId === "hydro" && hydroBlockTurns > 0;

  // Bug #10: compute aura-modified energy/pollution/toll for display.
  // Uses rule.energy/pollution/toll as baseline (era-specific values).
  const energyBreakdown = computeEnergyTick(facilityId, auraState, seatIdx, rule.energy);
  const pollutionBreakdown = computePollutionTick(facilityId, auraState, seatIdx, rule.pollution);
  const tollBreakdown = computeTollOnLanding(facilityId, auraState, seatIdx, rule.toll);
  const displayEnergy = energyBreakdown.effective;
  const displayPollution = pollutionBreakdown.effective;
  const displayToll = tollBreakdown.effective;
  const energyAuraModified = energyBreakdown.matched.length > 0 && displayEnergy !== rule.energy;
  const pollutionAuraModified =
    pollutionBreakdown.matched.length > 0 && displayPollution !== rule.pollution;
  const tollAuraModified = tollBreakdown.matched.length > 0 && displayToll !== rule.toll;
  const isOccupied = tileAlreadyBuilt;
  const effectiveCost = breakdown.effectiveCost;
  const hasAuraDiscount = breakdown.matched.length > 0 && effectiveCost !== rule.cost;
  const canAfford = tokens >= effectiveCost;

  // Category CSS class for left border + badge — fall back to "neutral" for
  // FacilityTypes not in static FACILITIES (defensive; shouldn't happen now).
  const category = facility?.category ?? "Fossil";
  const catRowClass =
    category === "Fossil"
      ? styles.catFossil
      : category === "Renewable" || category === "Geothermal"
        ? styles.catRenewable
        : styles.catClean;

  const catBadgeClass =
    category === "Fossil"
      ? styles.catBadgeFossil
      : category === "Renewable" || category === "Geothermal"
        ? styles.catBadgeRenewable
        : styles.catBadgeNuclear;

  const rowClass = [
    styles.facilityRow,
    catRowClass,
    isOccupied ? styles.facilityRowLocked : "",
    isEraLocked ? styles.facilityRowEraLocked : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Build button state — no illegal branch (getAvailableFacilities already filters).
  type BtnVariant = "build" | "unaffordable" | "owned";
  let btnVariant: BtnVariant = "build";
  if (isOccupied) btnVariant = "owned";
  else if (!canAfford) btnVariant = "unaffordable";

  const btnClass = [
    styles.btnBuild,
    btnVariant === "unaffordable" ? styles.btnBuildUnaffordable : "",
    btnVariant === "owned" ? styles.btnBuildOwned : "",
  ]
    .filter(Boolean)
    .join(" ");

  // btnLabel is computed inside FacilityRow which doesn't have access to useI18n
  // directly; occupied state label uses translate(lang, "buildMenu.tileOccupied").
  // For consistency we use the translate(lang, ...) pattern (same as lockedEra).
  const btnLabel = isOccupied
    ? translate(lang, "buildMenu.tileOccupied")
    : isHydroBlocked
      ? `Drought — blocked ${hydroBlockTurns} turn${hydroBlockTurns === 1 ? "" : "s"}`
      : !canAfford
        ? `Pay ${effectiveCost} tokens`
        : hasAuraDiscount
          ? `▶ Build (${effectiveCost} tokens)`
          : "▶ Build";

  const taglineClass = styles.tagline;
  const taglineText = isOccupied
    ? `Tile occupied — remove ${existingFacilityName ?? "current"} first`
    : (facility?.tagline ?? `${rule.facilityType} facility`);

  const costFinalClass = [
    styles.costFinal,
    !canAfford && !isOccupied ? styles.costFinalUnaffordable : "",
  ]
    .filter(Boolean)
    .join(" ");

  const iconSrc = facility?.iconSrc ?? "/images/facilities/facility_coal.png";

  return (
    <div
      className={rowClass}
      data-testid={`facility-row-${facilityId}`}
      aria-disabled={isEraLocked ? "true" : undefined}
    >
      <div className={styles.iconCell}>
        <img
          className={styles.facilityIcon}
          src={iconSrc}
          alt={rule.label}
          onError={(e) => {
            // Fallback: hide broken image silently (no-op)
            (e.currentTarget as HTMLImageElement).style.opacity = "0.3";
          }}
        />
      </div>

      <div className={styles.facilityMeta}>
        <div className={styles.nameRow}>
          <div className={styles.facilityName}>{rule.label}</div>
          <div className={catBadgeClass}>{category}</div>
        </div>
        <div className={taglineClass}>{taglineText}</div>
        {/* WEB.4.UI.11c: stats display restored — onBuild now snapshots these
            into per-tile metadata so tick/toll downstream read the same values.
            Bug #10: aura-modified values shown with * indicator when active. */}
        <div className={styles.statsRow}>
          <span
            className={`${styles.statItem} ${styles.statEnergy}`}
            title={
              energyAuraModified
                ? `Energy ${rule.energy} → ${displayEnergy} (aura: ${energyBreakdown.matched.map((m) => m.source).join(", ")})`
                : "Energy generated per turn"
            }
          >
            ⚡ {displayEnergy}
            {energyAuraModified ? (
              <span className={styles.auraDelta} data-testid="aura-delta-energy">
                {energyBreakdown.matched
                  .map((m) => {
                    const delta = m.add !== 0 ? (m.add > 0 ? `+${m.add}` : `${m.add}`) : null;
                    return `${delta ?? "×"} ${m.source}`;
                  })
                  .join(" ")}
              </span>
            ) : null}
          </span>
          <span
            className={`${styles.statItem} ${styles.statPollution}`}
            title={
              pollutionAuraModified
                ? `Pollution ${rule.pollution} → ${displayPollution} (aura: ${pollutionBreakdown.matched.map((m) => m.source).join(", ")})`
                : "Pollution per turn"
            }
          >
            ☠ +{displayPollution}
            {pollutionAuraModified ? (
              <span className={styles.auraDelta} data-testid="aura-delta-pollution">
                {pollutionBreakdown.matched
                  .map((m) => {
                    const delta = m.add !== 0 ? (m.add > 0 ? `+${m.add}` : `${m.add}`) : null;
                    return `${delta ?? "×"} ${m.source}`;
                  })
                  .join(" ")}
              </span>
            ) : null}
          </span>
          <span
            className={`${styles.statItem} ${styles.statToll}`}
            title={
              tollAuraModified
                ? `Toll ${rule.toll} → ${displayToll} (aura: ${tollBreakdown.matched.map((m) => m.source).join(", ")})`
                : "Toll opponents pay to land here"
            }
          >
            💰 {displayToll} tokens
            {tollAuraModified ? (
              <span className={styles.auraDelta} data-testid="aura-delta-toll">
                {tollBreakdown.matched
                  .map((m) => {
                    const delta = m.add !== 0 ? (m.add > 0 ? `+${m.add}` : `${m.add}`) : null;
                    return `${delta ?? "×"} ${m.source}`;
                  })
                  .join(" ")}
              </span>
            ) : null}
          </span>
        </div>
      </div>

      <div className={styles.costCol}>
        <div className={styles.costBase}>
          {translate(lang, "buildMenu.baseCost", { cost: rule.cost })}
        </div>
        <div
          className={costFinalClass}
          data-testid={`facility-cost-${facilityId}`}
          data-aura-discount={hasAuraDiscount ? "true" : "false"}
        >
          {`${effectiveCost} tokens total`}
        </div>
        {hasAuraDiscount ? (
          <div
            className={styles.auraChip ?? ""}
            data-testid={`facility-aura-chip-${facilityId}`}
            title={breakdown.matched.map((m) => m.source).join(", ")}
          >
            {effectiveCost < rule.cost ? "−" : "+"}
            {Math.abs(rule.cost - effectiveCost)} tokens aura
          </div>
        ) : null}
      </div>

      <div className={styles.actionCol}>
        {isEraLocked ? (
          /* R3 Bug 3: era-locked — show badge div, no button, click is no-op */
          <div
            className={styles.lockedEraBadge}
            aria-disabled="true"
            data-testid={`btn-build-${facilityId}`}
            title={translate(lang, "buildMenu.lockedEra", { era: lockedUntilEra as number })}
          >
            {translate(lang, "buildMenu.lockedEra", { era: lockedUntilEra as number })}
          </div>
        ) : (
          <button
            type="button"
            className={btnClass}
            disabled={isOccupied || !canAfford || isHydroBlocked}
            title={isHydroBlocked ? "Drought card blocks Hydro builds" : undefined}
            onClick={() =>
              onBuild(
                facilityId,
                effectiveCost,
                {
                  // Bug #10: snapshot aura-modified values so endTurn tick and toll
                  // downstream use the same numbers shown in the build preview.
                  energyPerTurn: displayEnergy,
                  pollutionPerTurn: displayPollution,
                  tollPerLand: displayToll,
                  builtInEra: era,
                  builtOnTerrain: terrain,
                },
                selectedDiscountKind,
              )
            }
            data-testid={`btn-build-${facilityId}`}
          >
            {btnLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DiscountPicker sub-component (R11.2.6)
// ---------------------------------------------------------------------------

interface DiscountPickerProps {
  eligibleDiscounts: ReadonlyArray<import("@gdg/rules-engine").PendingEffect>;
  /** "skip" = player chose full price; null = auto (but picker only shows when discounts exist) */
  selected: DiscountOverride;
  onChange: (override: DiscountOverride) => void;
  lang: "en" | "th";
}

function DiscountPicker({
  eligibleDiscounts,
  selected,
  onChange,
  lang,
}: DiscountPickerProps): React.ReactElement {
  return (
    <div className={styles.discountPicker ?? "discountPicker"} data-testid="discount-picker">
      <span className={styles.discountLabel ?? "discountLabel"}>
        {translate(lang, "buildMenu.discountCards")}
      </span>
      {eligibleDiscounts.map((pe, idx) => {
        const isSelected = selected === pe.kind;
        const label =
          pe.kind === "NextBuildFree" || pe.kind === "NextSolarFree"
            ? `${pe.sourceCardName} — FREE`
            : `${pe.sourceCardName} − ${pe.valueInt} tokens`;
        return (
          <button
            key={`${pe.kind}-${idx}`}
            type="button"
            className={
              isSelected
                ? (styles.discountPillActive ?? "discountPillActive")
                : (styles.discountPill ?? "discountPill")
            }
            data-testid={`discount-pill-${pe.kind}`}
            onClick={() => onChange(isSelected ? null : pe.kind)}
          >
            {label}
          </button>
        );
      })}
      <button
        type="button"
        className={
          selected === DISCOUNT_SKIP
            ? (styles.discountPillActive ?? "discountPillActive")
            : (styles.discountPill ?? "discountPill")
        }
        data-testid="discount-skip"
        onClick={() => onChange(selected === DISCOUNT_SKIP ? null : DISCOUNT_SKIP)}
      >
        Skip — full price
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BuildMenu — public component
// ---------------------------------------------------------------------------

export function BuildMenu(): React.ReactElement | null {
  const buildMenuOpen = useGameStore((s) => s.buildMenuOpen);
  // R11.2 — gate behind board-walk so the menu doesn't flash before arrival.
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const tokens = useGameStore((s) => s.tokens);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);
  const builtFacilityMeta = useGameStore((s) => s.builtFacilityMeta);
  const closeBuildMenu = useGameStore((s) => s.closeBuildMenu);
  const buildFacility = useGameStore((s) => s.buildFacility);
  // R20-fix (codex-review #4): Drought TurnBlock — disable Hydro rows when active.
  const hydroBlockTurns = useGameStore((s) => s.hydroBlockTurns);
  // WEB.4.SS.5: voluntary facility demolish
  const demolishOwnFacility = useGameStore((s) => s.demolishOwnFacility);
  // WEB.4.UI.8b: BuildCost auras applied to cost preview.
  const auraState = useGameStore((s) => s.auraState);
  // R11.1 fix #3: peqState consulted so NextBuildFree overrides aura -2.
  const peqState = useGameStore((s) => s.peqState);
  // R9: per-seat archetype perk drives build cost ±2 modifier.
  const playerPerks = useGameStore((s) => s.playerPerks);
  // WEB.4.UI.11b.2: era determines which facilities are unlocked.
  const era = useGameStore((s) => s.era);
  // R3 Bug 3: lang for era-lock badge translation.
  const lang = useGameStore((s) => s.lang) as "en" | "th";

  // WEB.4.R3.8 — Takeover mechanic
  const openTakeoverConfirm = useGameStore((s) => s.openTakeoverConfirm);
  const closeTakeoverConfirm = useGameStore((s) => s.closeTakeoverConfirm);
  const confirmTakeover = useGameStore((s) => s.confirmTakeover);
  const takeoverConfirmOpen = useGameStore((s) => s.takeoverConfirmOpen);
  const takeoverAmounts = useGameStore((s) => s.takeoverAmounts);
  const takeoverPrevOwner = useGameStore((s) => s.takeoverPrevOwner);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  // R11.2.6 — Discount picker local state.
  // null = auto-best (default: show effective cost with any free discount applied).
  // DISCOUNT_SKIP = player explicitly wants full price.
  // PendingKind = player picked a specific discount to apply.
  const [discountOverride, setDiscountOverride] = useState<DiscountOverride>(null);

  // R11.2.6 codex HIGH 3 fix — reset discount picker to auto-best every time
  // the menu opens. Component stays mounted (returns null when closed) so
  // setState from a previous session would otherwise persist across opens.
  useEffect(() => {
    if (buildMenuOpen) setDiscountOverride(null);
  }, [buildMenuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!buildMenuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeBuildMenu();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [buildMenuOpen, closeBuildMenu]);

  // WEB.4.R3.8 — Sync native <dialog> open/close with store state
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (takeoverConfirmOpen && !el.open) el.showModal();
    else if (!takeoverConfirmOpen && el.open) el.close();
  }, [takeoverConfirmOpen]);

  if (!buildMenuOpen) return null;
  if (boardAnimationActive) return null;

  const seatIdx = currentSeat as SeatIndex;
  const tileIdx = playerTiles[seatIdx] ?? 0;
  const tileType = _tileTypeByIndex[tileIdx] ?? "Unknown";
  const tileAlreadyBuilt = builtFacilities[tileIdx] !== undefined;
  const existingFacilityId = builtFacilities[tileIdx];
  const existingFacilityName = FACILITIES.find((f) => f.id === existingFacilityId)?.name;
  const seatTokens = tokens[seatIdx] ?? 0;
  // WEB.4.SS.5: demolish allowed only if seat OWNS the facility on this tile.
  const isOwnFacility = tileAlreadyBuilt && ownedBy[tileIdx] === seatIdx;
  const builtCost =
    builtFacilityMeta[tileIdx]?.builtCost ??
    FACILITIES.find((f) => f.id === existingFacilityId)?.baseCost ??
    0;
  const refundAmount = Math.ceil(builtCost * 0.5);

  // WEB.4.R3.8 — Takeover: player landed on opponent-owned facility
  const isOpponentFacility =
    tileAlreadyBuilt && ownedBy[tileIdx] !== undefined && ownedBy[tileIdx] !== seatIdx;
  // Preview cost for hint text (before confirm dialog opens)
  const takeoverPreviewCost = isOpponentFacility ? Math.round(builtCost * 1.5) : 0;

  const headerClass = SEAT_HEADER_CLASSES[seatIdx] ?? styles.header;
  const chipClass = SEAT_CHIP_CLASSES[seatIdx] ?? styles.seatChip;

  // R11.2.6: enumerate eligible discounts for this seat.
  const eligibleDiscounts = getEligibleDiscounts(peqState, seatIdx);
  const hasDiscounts = eligibleDiscounts.length > 0;

  // R11.2.6 codex HIGH 1 fix: the discount kind to forward to buildFacility is
  // the one ACTUALLY APPLIED to the row's breakdown — not the raw picker state.
  // When the picker is on auto-best (null), the row's breakdown.appliedDiscountKind
  // tells us which PEQ entry was used; pass that so the store drains it.
  // "skip" still maps to null (no PEQ entry consumed).
  function handleBuild(
    facilityId: string,
    cost: number,
    meta: {
      energyPerTurn: number;
      pollutionPerTurn: number;
      tollPerLand: number;
      builtInEra: 1 | 2 | 3 | 4;
      builtOnTerrain: string;
    },
    chosenDiscountKind: PendingKind | null,
  ): void {
    playSfx("build");
    buildFacility(facilityId, cost, meta, chosenDiscountKind);
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.target === e.currentTarget) closeBuildMenu();
  }

  function handleOverlayKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) closeBuildMenu();
  }

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      onKeyDown={handleOverlayKeyDown}
      data-testid="build-menu-overlay"
    >
      <div className={styles.panel} data-testid="build-menu-panel">
        {/* HEADER */}
        <div className={headerClass} data-testid="build-menu-header">
          <div>
            <span className={styles.tileName}>Tile {String(tileIdx).padStart(2, "0")}</span>
            <span className={styles.tileBiome}>· {tileType.toUpperCase()}</span>
          </div>
          <div className={styles.seatInfo}>
            <div className={chipClass} data-testid="build-menu-seat-chip">
              {SEAT_DISPLAY[seatIdx]}
            </div>
            <div className={styles.tokenDisplay}>
              <div className={styles.coinIcon} />
              <span data-testid="build-menu-tokens">{seatTokens} tokens</span>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={closeBuildMenu}
            aria-label="Close build menu"
            data-testid="build-menu-close"
          >
            ✕
          </button>
        </div>

        {/* SUBHEADER */}
        <div className={styles.subheader} aria-hidden="true">
          <div>{translate(lang, "buildMenu.colHeader.icon")}</div>
          <div>{translate(lang, "buildMenu.colHeader.facility")}</div>
          <div style={{ textAlign: "right" }}>{translate(lang, "buildMenu.colHeader.cost")}</div>
          <div style={{ textAlign: "right" }}>{translate(lang, "buildMenu.colHeader.action")}</div>
        </div>

        {/* R11.2.6: Discount picker — shown only when eligible discounts exist */}
        {hasDiscounts ? (
          <DiscountPicker
            eligibleDiscounts={eligibleDiscounts}
            selected={discountOverride}
            onChange={setDiscountOverride}
            lang={lang}
          />
        ) : null}

        {/* BODY — facility rows (R3 Bug 3: ALL facilities for terrain, era-locked shown greyed) */}
        <div className={styles.body} data-testid="build-menu-body">
          {getAllFacilitiesForTerrain(tileType).map((entry) => {
            const facilityId =
              FACILITY_TYPE_TO_ID[entry.facilityType] ?? entry.facilityType.toLowerCase();
            const facility = FACILITIES.find((f) => f.id === facilityId);
            const isEraLocked = entry.minEra > era;
            // R11.2.6: pass discountOverride so breakdown reflects player's selection.
            // Use entry as rule — for era-locked rows cost/stats come from the unlock era rule.
            const breakdown = computeBuildCostBreakdown(
              facilityId,
              entry.cost,
              auraState,
              seatIdx,
              playerPerks[seatIdx],
              peqState,
              discountOverride,
            );
            return (
              <FacilityRow
                key={`${entry.facilityType}-${entry.label}`}
                rule={entry}
                facility={facility}
                facilityId={facilityId}
                tileAlreadyBuilt={tileAlreadyBuilt}
                existingFacilityName={existingFacilityName}
                tokens={seatTokens}
                breakdown={breakdown}
                auraState={auraState}
                seatIdx={seatIdx}
                era={era}
                terrain={tileType}
                {...(isEraLocked ? { lockedUntilEra: entry.minEra } : {})}
                lang={lang}
                hydroBlockTurns={hydroBlockTurns[seatIdx] ?? 0}
                selectedDiscountKind={breakdown.appliedDiscountKind}
                onBuild={handleBuild}
              />
            );
          })}
        </div>

        {/* FOOTER */}
        <div className={styles.footer} data-testid="build-menu-footer">
          <div className={styles.footerHint}>
            {isOpponentFacility
              ? `Opponent facility. Take over for ${takeoverPreviewCost} tokens?`
              : tileAlreadyBuilt
                ? isOwnFacility
                  ? `Your facility. Demolish to refund ${refundAmount} tokens (50% of build cost).`
                  : "Build complete. Press Leave Tile, or Close to dismiss."
                : "Select a facility, or skip to keep your tokens."}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {/* WEB.4.SS.5: Demolish button — only when seat owns facility here */}
            {isOwnFacility ? (
              <button
                type="button"
                className={styles.btnSkip}
                onClick={() => {
                  demolishOwnFacility();
                }}
                data-testid="btn-demolish"
                style={{ background: "#aa3a2a", color: "#fff" }}
                title={`Demolish ${existingFacilityName} and refund ${refundAmount} tokens.`}
              >
                Demolish (+{refundAmount} tokens)
              </button>
            ) : null}
            {/* WEB.4.R3.8: Take Over button — only when on opponent facility.
                R7 #8: enabled even when unaffordable; openTakeoverConfirm fires
                InsufficientFundsToast instead of silently doing nothing. */}
            {isOpponentFacility ? (
              <button
                type="button"
                className={styles.btnTakeover}
                onClick={openTakeoverConfirm}
                data-testid="btn-takeover"
                title={`Pay ${takeoverPreviewCost} tokens to seize this facility`}
              >
                Take Over ({takeoverPreviewCost} tokens)
              </button>
            ) : null}
            <button
              type="button"
              className={styles.btnSkip}
              onClick={closeBuildMenu}
              data-testid="btn-skip-build"
            >
              {tileAlreadyBuilt ? "Close" : "Skip Build"}
            </button>
          </div>
        </div>
      </div>

      {/* WEB.4.R3.8 — Takeover confirmation dialog (native <dialog> element) */}
      <dialog ref={dialogRef} className={styles.takeoverDialog} data-testid="takeover-dialog">
        <div className={styles.takeoverDialogHeader}>
          {translate(lang, "buildMenu.seizeFacility")}
        </div>
        <div className={styles.takeoverDialogBody}>
          <div className={styles.takeoverSplit}>
            <span className={styles.takeoverSplitLabel}>You pay</span>
            <span className={styles.takeoverSplitValue}>
              {takeoverAmounts?.totalCost ?? 0} tokens
            </span>
          </div>
          <div className={styles.takeoverSplit}>
            <span className={styles.takeoverSplitLabel}>
              P{(takeoverPrevOwner ?? 0) + 1} receives
            </span>
            <span className={styles.takeoverSplitValue}>
              {takeoverAmounts?.prevOwnerCredit ?? 0} tokens
            </span>
          </div>
          <div className={styles.takeoverSplit}>
            <span className={styles.takeoverSplitLabel}>Burned (bank)</span>
            <span className={`${styles.takeoverSplitValue} ${styles.takeoverSplitBurned}`}>
              {takeoverAmounts?.burned ?? 0} tokens
            </span>
          </div>
        </div>
        <div className={styles.takeoverDialogFooter}>
          <button
            type="button"
            className={styles.btnTakeoverCancel}
            onClick={closeTakeoverConfirm}
            data-testid="btn-takeover-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnTakeoverConfirm}
            onClick={() => {
              confirmTakeover();
              closeBuildMenu();
            }}
            data-testid="btn-takeover-confirm"
          >
            {translate(lang, "buildMenu.seizeIt")}
          </button>
        </div>
      </dialog>
    </div>
  );
}
